import * as Accordion from '@radix-ui/react-accordion';
import classNames from 'classnames';
import { forwardRef, useState, type ReactNode } from 'react';

import { Typography } from '@/components';
import { ArrowDownSLineIcon } from '@/components/icons/react/arrow-down-s-line';
import { PromoCtaPill } from '@/theme/header/components/PromoCtaPill/PromoCtaPill';
import { getRel, getTarget } from '@/theme/shared/utils';

import styles from './MobileMenuAccordion.module.scss';
import { hasChildren } from '../../../utils';

import type { HeaderMenuItem } from '../../../types';

type MobileMenuAccordionProps = {
  items: HeaderMenuItem[];
  onNavigate: () => void;
};

function trackMobileMenu(menuItem: string, menuLevel: 'top' | 'sub', parentItem?: string) {
  window.InteractTracking?.track('menu_interaction', {
    menu_item_name: menuItem,
    menu_level: menuLevel,
    parent_item: parentItem,
  });
}

export function MobileMenuAccordion({ items, onNavigate }: MobileMenuAccordionProps) {
  // Controlled so each panel knows whether it is open: force-mounted content has to
  // drop its inert attribute when it opens, and Radix reports state on the DOM node
  // rather than in the props it passes down.
  const [openValue, setOpenValue] = useState('');

  if (items.length === 0) {
    return null;
  }

  return (
    <Accordion.Root
      className={styles.accordion}
      type="single"
      collapsible
      value={openValue}
      onValueChange={(value) => {
        setOpenValue(value);

        // Mirrors the desktop panel-open event; collapsing reports nothing.
        if (!value) {
          return;
        }

        const opened = items.find((entry) => `item-${entry.id}` === value);

        if (opened) {
          trackMobileMenu(opened.title, 'top');
        }
      }}
    >
      {items.map((item) =>
        hasChildren(item) ? (
          <AccordionItem
            key={item.id}
            item={item}
            isOpen={openValue === `item-${item.id}`}
            onNavigate={onNavigate}
          />
        ) : (
          <StandaloneLink key={item.id} item={item} onNavigate={onNavigate} />
        ),
      )}
    </Accordion.Root>
  );
}

type AccordionItemProps = {
  item: HeaderMenuItem;
  isOpen: boolean;
  onNavigate: () => void;
};

function AccordionItem({ item, isOpen, onNavigate }: AccordionItemProps) {
  const sections = item.children ?? [];

  return (
    <Accordion.Item value={`item-${item.id}`} className={styles.item}>
      <MobileAccordionTrigger>
        <Typography variant="label" size="medium" tagName="span" color="inherit">
          {item.title}
        </Typography>
      </MobileAccordionTrigger>
      <MobileAccordionContent open={isOpen}>
        <div className={styles.panel}>
          <div className={styles.sections}>
            {sections.map((section) => (
              <MobileSection key={section.id} section={section} onNavigate={onNavigate} />
            ))}
          </div>
          <MobilePromo promo={item.promo} onNavigate={onNavigate} />
        </div>
      </MobileAccordionContent>
    </Accordion.Item>
  );
}

type StandaloneLinkProps = {
  item: HeaderMenuItem;
  onNavigate: () => void;
};

function StandaloneLink({ item, onNavigate }: StandaloneLinkProps) {
  const handleClick = () => {
    trackMobileMenu(item.title, 'top');
    onNavigate();
  };

  return (
    <div className={styles.linkItem}>
      <a
        className={styles.link}
        href={item.url || '#'}
        target={getTarget(item.target)}
        rel={getRel(item.target)}
        onClick={handleClick}
      >
        <Typography variant="label" size="medium" tagName="span" color="inherit">
          {item.title}
        </Typography>
      </a>
    </div>
  );
}

type MobileSectionProps = {
  section: HeaderMenuItem;
  onNavigate: () => void;
};

function MobileSection({ section, onNavigate }: MobileSectionProps) {
  const childLinks = section.children ?? [];

  if (childLinks.length === 0) {
    return (
      <div className={styles.sectionStandalone}>
        <a
          className={styles.sectionStandaloneLink}
          href={section.url || '#'}
          target={getTarget(section.target)}
          rel={getRel(section.target)}
          onClick={() => {
            trackMobileMenu(section.title, 'sub', section.title);
            onNavigate();
          }}
        >
          <Typography singleLine size="medium" tagName="span" color="inherit">
            {section.title}
          </Typography>
        </a>
      </div>
    );
  }

  return (
    <section className={styles.section} aria-label={section.title || undefined}>
      {!section.hideLabel && section.title ? (
        <Typography
          variant="label"
          size="small"
          color="var(--color-text-brand-primary)"
          className={styles.sectionLabel}
          data-label={section.iconKey || section.title || undefined}
        >
          {section.title}
        </Typography>
      ) : null}
      <ul className={styles.sectionLinks}>
        {childLinks.map((link) => (
          <li key={link.id}>
            <a
              className={styles.sectionLink}
              href={link.url || '#'}
              target={getTarget(link.target)}
              rel={getRel(link.target)}
              data-overview={link.overview ? true : undefined}
              aria-current={link.current ? 'page' : undefined}
              onClick={() => {
                trackMobileMenu(link.title, 'sub', section.title);
                onNavigate();
              }}
            >
              <Typography tagName="span" singleLine size="medium" color="inherit">
                {link.title}
              </Typography>
            </a>
          </li>
        ))}
      </ul>
    </section>
  );
}

type MobilePromoProps = {
  promo?: HeaderMenuItem['promo'];
  onNavigate: () => void;
};

function MobilePromo({ promo, onNavigate }: MobilePromoProps) {
  if (
    !promo ||
    (!promo.title &&
      !promo.heading &&
      !promo.description &&
      !promo.ctaLabel &&
      !promo.ctaOverlay &&
      !promo.image)
  ) {
    return null;
  }

  const promoImage = promo.image;
  const promoAlt = promoImage?.alt ?? promo.title ?? 'Promotional highlight';

  const card = (
    <article
      className={styles.promoCard}
      data-tint={promo.pill || undefined}
      data-compact={promo.compact || undefined}
      data-heading={promo.heading ? '' : undefined}
      data-cta={promo.ctaOverlay ? '' : undefined}
      style={promo.maxHeight ? { maxHeight: promo.maxHeight } : undefined}
    >
      {promoImage ? (
        <img
          className={styles.promoImage}
          src={promoImage.url}
          alt={promoAlt}
          width={promoImage.width}
          height={promoImage.height}
          loading="lazy"
        />
      ) : (
        <div className={styles.promoPlaceholder} aria-hidden="true"></div>
      )}

      {promo.heading && (
        <div className={styles.promoHeading} dangerouslySetInnerHTML={{ __html: promo.heading }} />
      )}

      {promo.ctaOverlay && <PromoCtaPill label={promo.ctaOverlay} />}

      {promo.pill && promo.ctaLabel && (
        <Typography
          tagName="span"
          variant="eyebrow"
          size="small"
          color="inherit"
          className={styles.promoPill}
        >
          {promo.ctaLabel}
        </Typography>
      )}

      {(promo.title || promo.description || (!promo.pill && promo.ctaLabel)) && (
        <div className={styles.promoContent}>
          {!promo.pill && promo.ctaLabel && (
            <Typography
              variant="eyebrow"
              size="small"
              tagName="span"
              color="var(--color-text-default-soft-light)"
              className={styles.promoEyebrow}
            >
              {promo.ctaLabel}
            </Typography>
          )}
          {promo.title && (
            <Typography variant="eyebrow" color="var(--color-text-default-default-light)">
              {promo.title}
            </Typography>
          )}
          {promo.description && (
            <Typography variant="eyebrow" size="small" color="var(--color-text-default-soft-light)">
              {promo.description}
            </Typography>
          )}
        </div>
      )}
    </article>
  );

  if (promo.ctaUrl) {
    return (
      <a
        className={styles.promoLink}
        href={promo.ctaUrl}
        target={getTarget(promo.ctaTarget)}
        rel={getRel(promo.ctaTarget)}
        data-compact={promo.compact || undefined}
        onClick={() => {
          if (promo.ctaUrl?.includes('/guides/planning-deploying-successful-intranet/')) {
            window.InteractTracking?.track('p_and_d_guide_nav_download', {
              page_name: window.InteractTracking?.getContext().page_name || '',
            });
          }
          onNavigate();
        }}
      >
        {card}
      </a>
    );
  }

  return card;
}

type TriggerProps = Accordion.AccordionTriggerProps & {
  children: ReactNode;
};

const MobileAccordionTrigger = forwardRef<HTMLButtonElement, TriggerProps>(
  ({ children, className, ...props }, ref) => (
    <Accordion.Header className={styles.header}>
      <Accordion.Trigger {...props} ref={ref} className={classNames(styles.trigger, className)}>
        {children}
        <ArrowDownSLineIcon
          aria-hidden="true"
          focusable="false"
          className={`${styles.chevron} ix-icon-18`}
          width={18}
          height={18}
        />
      </Accordion.Trigger>
    </Accordion.Header>
  ),
);

MobileAccordionTrigger.displayName = 'MobileAccordionTrigger';

type ContentProps = Accordion.AccordionContentProps & {
  children: ReactNode;
  open: boolean;
};

const MobileAccordionContent = forwardRef<HTMLDivElement, ContentProps>(
  ({ children, className, open, ...props }, ref) => (
    <Accordion.Content
      {...props}
      ref={ref}
      forceMount
      inert={open ? undefined : ''}
      className={classNames(styles.content, className)}
    >
      <div className={styles.contentInner}>{children}</div>
    </Accordion.Content>
  ),
);

MobileAccordionContent.displayName = 'MobileAccordionContent';
